/**
 * Fantasy Console 0 - Code Templates
 *
 * Provides code templates and snippets for quick starts.
 */

import { useState } from 'react'
import styles from './CodeTemplates.module.css'
import modalStyles from '../../styles/modal.module.css'
import { CloseIcon } from '../shared/icons'

interface CodeTemplate {
  id: string
  name: string
  description: string
  code: string
}

const TEMPLATES: CodeTemplate[] = [
  {
    id: 'empty',
    name: 'Empty',
    description: 'Minimal starting point',
    code: `function _init() {
  // initialization
}

function _update() {
  // update game state
}

function _draw() {
  cls()
  // draw game
}`,
  },
  {
    id: 'hello-world',
    name: 'Hello World',
    description: 'Simple text display',
    code: `function _init() {
}

function _update() {
}

function _draw() {
  cls(0)
  print("Hello World!", 36, 60, 7)
}`,
  },
  {
    id: 'particle-system',
    name: 'Particle System',
    description: 'Colorful bouncing particles',
    code: `let particles = []

function _init() {
  for (let i = 0; i < 50; i++) {
    add(particles, {
      x: rnd(128),
      y: rnd(128),
      vx: rnd(2) - 1,
      vy: rnd(2) - 1,
      col: flr(rnd(16))
    })
  }
}

function _update() {
  foreach(particles, (p) => {
    p.x += p.vx
    p.y += p.vy

    if (p.x < 0 || p.x > 127) p.vx *= -1
    if (p.y < 0 || p.y > 127) p.vy *= -1
  })
}

function _draw() {
  cls(0)
  foreach(particles, (p) => {
    pset(p.x, p.y, p.col)
  })
  print("particles: " + count(particles), 2, 2, 7)
}`,
  },
  {
    id: 'starfield',
    name: 'Starfield',
    description: '3D starfield effect',
    code: `let stars = []

function _init() {
  for (let i = 0; i < 100; i++) {
    add(stars, {
      x: rnd(128),
      y: rnd(128),
      z: rnd(4) + 0.5
    })
  }
}

function _update() {
  foreach(stars, (s) => {
    s.x -= s.z
    if (s.x < 0) {
      s.x = 128
      s.y = rnd(128)
      s.z = rnd(4) + 0.5
    }
  })
}

function _draw() {
  cls(0)
  foreach(stars, (s) => {
    let col = s.z > 2 ? 7 : s.z > 1 ? 6 : 5
    pset(s.x, s.y, col)
  })
}`,
  },
  {
    id: 'bouncing-ball',
    name: 'Bouncing Ball',
    description: 'Physics-based bouncing ball',
    code: `let ball = {
  x: 64,
  y: 20,
  vx: 2,
  vy: 0,
  r: 6
}

const gravity = 0.2
const bounce = 0.85
const friction = 0.99

function _init() {
}

function _update() {
  // apply gravity
  ball.vy += gravity

  // move ball
  ball.x += ball.vx
  ball.y += ball.vy

  // bounce off walls
  if (ball.x - ball.r < 0) {
    ball.x = ball.r
    ball.vx *= -bounce
  }
  if (ball.x + ball.r > 127) {
    ball.x = 127 - ball.r
    ball.vx *= -bounce
  }

  // bounce off ceiling
  if (ball.y - ball.r < 0) {
    ball.y = ball.r
    ball.vy *= -bounce
  }
  
  // bounce off floor
  if (ball.y + ball.r > 127) {
    ball.y = 127 - ball.r
    ball.vy *= -bounce
    ball.vx *= friction
  }

  // Bounce the ball up when A is pressed
  if (btnp(5)) {
    ball.vy -= flr(rnd(3) + 3)
    ball.vx += flr(rnd(6) - 3)
  }
}

function _draw() {
  cls(1)
  circfill(ball.x, ball.y, ball.r, 8)
  circ(ball.x, ball.y, ball.r, 2)
}`,
  },
]

interface CodeTemplatesProps {
  onSelect: (code: string) => void
  onClose: () => void
}

export function CodeTemplates({ onSelect, onClose }: CodeTemplatesProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const handleApply = () => {
    const template = TEMPLATES.find((t) => t.id === selectedId)
    if (template) {
      onSelect(template.code)
      onClose()
    }
  }

  return (
    <div className={modalStyles.modalOverlay} onClick={onClose}>
      <div
        className={`${modalStyles.modalDialog} ${styles.modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.modalHeader}>
          <h3 className={modalStyles.modalTitle}>Code Templates</h3>
          <button className={modalStyles.modalCloseButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.list}>
            {TEMPLATES.map((template) => (
              <button
                key={template.id}
                className={`${styles.item} ${selectedId === template.id ? styles.selected : ''}`}
                onClick={() => setSelectedId(template.id)}
              >
                <span className={styles.itemName}>{template.name}</span>
                <span className={styles.itemDescription}>
                  {template.description}
                </span>
              </button>
            ))}
          </div>

          <div className={styles.preview}>
            {selectedId ? (
              <pre className={styles.code}>
                {TEMPLATES.find((t) => t.id === selectedId)?.code}
              </pre>
            ) : (
              <div className={styles.emptyPreview}>
                Select a template to preview
              </div>
            )}
          </div>
        </div>

        <div className={`${modalStyles.modalFooter} ${styles.footer}`}>
          <p className={styles.warning}>This will replace your current code</p>
          <div className={modalStyles.modalActions}>
            <button className='btn btn-secondary' onClick={onClose}>
              Cancel
            </button>
            <button
              className='btn btn-primary'
              onClick={handleApply}
              disabled={!selectedId}
            >
              Apply Template
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { TEMPLATES }
